import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer'

/**
 * Google Map Component
 */
export class GMXGoogleMap extends HTMLElement {
	private _map: google.maps.Map
	private _geocoder: google.maps.Geocoder
	private _markers: google.maps.marker.AdvancedMarkerElement[] = []
	private _clusterer: MarkerClusterer
	private _viewportPadding: google.maps.Padding = { top: 0, right: 0, bottom: 0, left: 0 }

	private _mapReady: Promise<void>
	private _markMapReady: () => void

	static observedAttributes = ['data-markers']

	constructor() {
		super()

		this._mapReady = new Promise<void>(resolve => {
			this._markMapReady = resolve
		})
	}

	get region(): string {
		// If map is used in dealer locator, get region from parent, otherwise get from self
		const dealerLocatorParent = this.closest('gmx-dealer-locator')
		return dealerLocatorParent && dealerLocatorParent.getAttribute('data-region')
			? dealerLocatorParent.getAttribute('data-region')
			: this.getAttribute('data-region')
	}
	set region(val: string) {
		this.setAttribute('data-region', val)
	}

	get language(): string {
		// If map is used in dealer locator, get language from parent, otherwise get from self
		const dealerLocatorParent = this.closest('gmx-dealer-locator')
		return dealerLocatorParent && dealerLocatorParent.getAttribute('data-language')
			? dealerLocatorParent.getAttribute('data-language')
			: this.getAttribute('data-language')
	}
	set language(val: string) {
		this.setAttribute('data-language', val)
	}

	get defaultLocation(): string {
		return this.getAttribute('data-default-location')
	}
	set defaultLocation(val: string) {
		this.setAttribute('data-default-location', val)
	}

	get mapId(): string {
		return this.getAttribute('data-map-id')
	}
	set mapId(val: string) {
		this.setAttribute('data-map-id', val)
	}

	get mapApiKey(): string {
		return this.getAttribute('data-map-api-key')
	}
	set mapApiKey(val: string) {
		this.setAttribute('data-map-api-key', val)
	}

	get markers(): google.maps.marker.AdvancedMarkerElementOptions[] {
		const mks = this.getAttribute('data-markers')
		return JSON.parse(mks) as google.maps.marker.AdvancedMarkerElementOptions[]
	}

	attributeChangedCallback(name: string, oldValue: string, newValue: string) {
		if (name === 'data-markers' && oldValue !== newValue) {
			this.setMarkers(this.markers)
		}
	}

	get map(): google.maps.Map {
		return this._map
	}

	/**
	 * Resolves once this element has finished trying to build its `google.maps.Map`.  Anything that
	 * drives the map — pins, panning, zooming — should wait on this rather than on
	 * `gmxGoogleMapApiLoaded`, which only says the Maps script arrived: the element still has to
	 * geocode its default location before it can construct the map.
	 *
	 * Resolves even when that fails and no map was built, so callers are never left hanging; the
	 * map methods below no-op in that case.
	 */
	whenReady(): Promise<void> {
		return this._mapReady
	}

	async connectedCallback(): Promise<void> {
		if (window.gmxGoogleMapApiLoaderSingleton?.loadStatus !== 'loaded') {
			window.addEventListener('gmxGoogleMapApiLoaded', this._mapLoaded.bind(this), { once: true })

			if (window.gmxGoogleMapApiLoaderSingleton) {
				window.gmxGoogleMapApiLoaderSingleton.loadMapApiFromAPIKey(this.mapApiKey, this.region, this.language)
			} else {
				window.addEventListener('gmxGoogleMapApiLoaderSingletonReady', () => {
					window.gmxGoogleMapApiLoaderSingleton.loadMapApiFromAPIKey(this.mapApiKey, this.region, this.language)
				}, { once: true })
			}
		} else {
			this._mapLoaded()
		}
	}

	private async _mapLoaded(): Promise<void> {
		this._geocoder = new google.maps.Geocoder()

		try {
			await this.initMap()
		} catch (err) {
			// Geocoding the default location can fail, which leaves this element without a map.
			// Report it and release anything waiting rather than stalling it on a map that is
			// never coming — the guards on the methods below keep those callers safe.
			window.console.error(`ERROR:  Google Map failed to initialize: ${err}`)
		} finally {
			this._markMapReady()
		}
	}

	async initMap(): Promise<void> {
		const location = await this.geocodeLocation(this.defaultLocation)

		// This is a hack to center in the middle of the united states
		const center = this.defaultLocation.toLowerCase() === 'united states'
			? new google.maps.LatLng(39.5, -98.35)
			: location.geometry.location

		this._map = new google.maps.Map(this, {
			center: center,
			controlSize: 28,
			disableDefaultUI: true,
			mapId: this.mapId,
			mapTypeId: google.maps.MapTypeId.ROADMAP,
			maxZoom: 17,
			zoom: 4,
			zoomControl: true,
			zoomControlOptions: {
				position: google.maps.ControlPosition.BOTTOM_LEFT,
			},
		})

		this._initializeCluster()

		if (this.markers && this.markers.length) {
			this.setMarkers(this.markers)
		}

		const myOverlay = new google.maps.OverlayView()
		myOverlay.draw = function () {
			this.getPanes().markerLayer.className = 'markerLayer'
		}
		myOverlay.setMap(this._map)
	}

	setMarkers(markers: google.maps.marker.AdvancedMarkerElementOptions[]): void {
		if (this._map) {
			this.clearMarkers()

			markers.forEach(m => {
				m.map = this._map
				if (!m.content) {
					const newNode = document.createElement('button')
					newNode.classList.add('gmx-pin-individual')

					m.content = newNode
				}
			})

			// Create markers from `MarkerOptions`
			const markersToAdd =
				(markers as google.maps.marker.AdvancedMarkerElementOptions[]).map(this._createMarker)

			this._clusterer.addMarkers(markersToAdd)

			const bounds = this.extendBounds(new google.maps.LatLngBounds(), markersToAdd)
			this._markers = markersToAdd
			this.fitBounds(bounds)
			this._map.setZoom(this._map.getZoom())
		}
	}

	/**
	 * Overlay to keep out of the way when fitting bounds, zooming into a cluster, or centering a
	 * point.  The map still fills its element; this only changes where those operations place the
	 * visible center — e.g. the dealer-locator search panel covering the right or bottom edge.
	 */
	public setViewportPadding(padding: google.maps.Padding): void {
		this._viewportPadding = {
			top: padding.top ?? 0,
			right: padding.right ?? 0,
			bottom: padding.bottom ?? 0,
			left: padding.left ?? 0,
		}
	}

	public fitBounds(bounds: google.maps.LatLngBounds, padding?: number | google.maps.Padding): void {
		if (this._map) {
			this._map.fitBounds(bounds, padding ?? this._viewportPadding)
		}
	}

	public panBy(x: number, y: number): void {
		if (this._map) {
			this._map.panBy(x, y)
		}
	}

	/**
	 * Removes all markers from the map.
	 */
	public clearMarkers(): void {
		this._clusterer.clearMarkers()

		this._markers = []
		// this._markerListeners = []
	}

	geocodeLocation(
		location: string | google.maps.LatLng | google.maps.LatLngLiteral,
		region?: string
	): Promise<google.maps.GeocoderResult> {
		return new Promise((resolve, reject) => {
			const geocoderRequest: google.maps.GeocoderRequest = (location as google.maps.LatLng).lat !== undefined
				? { location: location as google.maps.LatLng  }
				: {
					address: location as string,
					region: region || this.region,
				}

			this._geocoder.geocode(
				geocoderRequest,
				(result: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
					if (status === google.maps.GeocoderStatus.OK) {
						const geocodedLocation = result[0] // TODO: We should return the full array and let consumers decide which is the result they want
						resolve(geocodedLocation)
					} else {
						reject(status)
					}
				}
			)
		})
	}

	private _initializeCluster(): void {
		this._clusterer = new MarkerClusterer({
			algorithm: new SuperClusterAlgorithm({ maxZoom: 17, radius: 100 }),
			map: this._map,
			onClusterClick: (_event, cluster, map) => {
				if (cluster.bounds) {
					map.fitBounds(cluster.bounds, this._viewportPadding)
				}
			},
			renderer: {
				render: ({ count, position }) => {
					const newMarkerEl = document.createElement('button')

					if (count < 10) {
						newMarkerEl.classList.add('gmx-pin-group-small')
					} else {
						newMarkerEl.classList.add('gmx-pin-group-large')
					}

					// Create label
					const newMarkerLabel = document.createElement('span')
					newMarkerLabel.classList.add('gmx-pin-label')

					newMarkerLabel.innerText = String(count)
					newMarkerEl.appendChild(newMarkerLabel)

					return new google.maps.marker.AdvancedMarkerElement({
						content: newMarkerEl,
						position,
					})
				},
			},
		})
	}

	private _createMarker(markerOpts: google.maps.marker.AdvancedMarkerElementOptions): google.maps.marker.AdvancedMarkerElement {
		return new google.maps.marker.AdvancedMarkerElement(markerOpts)
	}

	extendBounds(
		bounds: google.maps.LatLngBounds,
		markers: google.maps.marker.AdvancedMarkerElement | google.maps.marker.AdvancedMarkerElement[]
	): google.maps.LatLngBounds {
		if (Array.isArray(markers)) {
			markers.forEach(m => bounds.extend(m.position))
		} else {
			bounds.extend(markers.position)
		}

		return bounds
	}

	/**
	 * Sets the map's center and zoom.
	 *
	 * Viewport padding is applied after centering so the point lands in the unobscured portion of
	 * the map rather than at the geometric center (which a side or bottom overlay can cover).
	 */
	setCenterAndZoom(center: google.maps.LatLng | google.maps.LatLngLiteral, zoom: number): void {
		if (this._map) {
			this._map.setZoom(zoom)
			this._map.setCenter(center)
			this._panForViewportPadding()
		}
	}

	/**
	 * Shift the current center into the middle of the area left after viewport padding.  Positive
	 * `panBy` x moves the center east and y south, which is what a right- or bottom-edge overlay
	 * needs so the point appears in the remaining view.
	 */
	private _panForViewportPadding(): void {
		const x = ((this._viewportPadding.right ?? 0) - (this._viewportPadding.left ?? 0)) / 2
		const y = ((this._viewportPadding.bottom ?? 0) - (this._viewportPadding.top ?? 0)) / 2

		if (x || y) {
			this._map.panBy(x, y)
		}
	}

	setZoom(zoom: number): void {
		if (this._map) {
			this._map.setZoom(zoom)
		}
	}
}

if (!customElements.get('gmx-google-map')) {
	customElements.define('gmx-google-map', GMXGoogleMap)
}
